import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { DashboardFilters, DatePreset } from '../types/dashboard.types';
import type { OverviewGranularity } from '@types';
import { ACTIVE_SESSION_STATUSES, DEFAULT_FILTERS, getDateRangeFromPreset } from '../types/dashboard.types';

interface DashboardState {
  filters: DashboardFilters;
  currentPage: number;
  rowsPerPage: number;
  overviewGranularity: OverviewGranularity;
  setFilters: (filters: Partial<DashboardFilters>) => void;
  setDatePreset: (preset: DatePreset) => void;
  setCustomDateRange: (from: Date | null, to: Date | null) => void;
  setCurrentPage: (page: number) => void;
  setRowsPerPage: (rows: number) => void;
  setOverviewGranularity: (granularity: OverviewGranularity) => void;
  /**
   * Filters the sessions list down to what the Active Sessions KPI counts.
   *
   * Clears the date range as well, and that is the point: the KPI ignores it,
   * so a list that kept it could show none of the sessions the tile just
   * reported. In QA 3 of the 4 active sessions started outside "Last 7 days".
   */
  showActiveSessions: () => void;
  /** Undoes `showActiveSessions`, restoring the range it replaced. */
  clearActiveSessions: () => void;
  clearFilters: () => void;
  resetPagination: () => void;
}

export const useDashboardStore = create<DashboardState>()(
  persist(
    (set) => ({
      filters: DEFAULT_FILTERS,
      currentPage: 1,
      rowsPerPage: 20,
      overviewGranularity: 'daily' as OverviewGranularity,

      setFilters: (newFilters) => {
        set((state) => ({
          filters: { ...state.filters, ...newFilters },
          currentPage: 1, // Reset page when filters change
        })); 
      },

      setDatePreset: (preset) => {
        set((state) => {
        // For 'custom', keep existing dates or set to null (user will select via calendar)
          if (preset === 'custom') {
            return {
              filters: {
                ...state.filters,
                dateRange: {
                  preset: 'custom',
                  from: state.filters.dateRange.from,
                  to: state.filters.dateRange.to,
                },
              },
              currentPage: 1,
            };
          }

          const dateRange = getDateRangeFromPreset(preset);
          return {
            filters: {
              ...state.filters,
              dateRange: {
                preset,
                from: dateRange.from,
                to: dateRange.to,
              },
            },
            currentPage: 1,
          };
        }); 
      },

      setCustomDateRange: (from, to) => {
        set((state) => ({
          filters: {
            ...state.filters,
            dateRange: {
              preset: 'custom',
              from,
              to,
            },
          },
          currentPage: 1,
        })); 
      },

      /**
       * The date range is deliberately NOT touched.
       *
       * It used to be blanked to `{ from: null, to: null, preset: 'custom' }`,
       * and that was wrong twice over: `custom` means "the user picked exact
       * dates", so `buildEffectiveFilters` stopped materialising the preset and
       * the overview asked for a window of now..now — every tile read zero. And
       * because `dateRange` is persisted, the broken value survived a reload
       * with no way to correct itself.
       *
       * Ignoring the range is a property of the REQUEST, not of the filters:
       * `getSessionsAsync` drops the date params when statuses are set. So
       * nothing has to be saved, restored, or undone.
       */
      showActiveSessions: () => {
        set((state) => ({
          filters: { ...state.filters, statuses: ACTIVE_SESSION_STATUSES },
          currentPage: 1,
        }));
      },

      clearActiveSessions: () => {
        set((state) =>
          state.filters.statuses.length === 0
            ? state
            : { filters: { ...state.filters, statuses: [] }, currentPage: 1 },
        );
      },

      setCurrentPage: (page) => {
        set({ currentPage: page }); 
      },

      setRowsPerPage: (rows) => {
        set({ rowsPerPage: rows, currentPage: 1 }); 
      },

      setOverviewGranularity: (granularity) => {
        set({ overviewGranularity: granularity });
      },

      clearFilters: () => {
        set((state) => ({
          filters: { ...DEFAULT_FILTERS, orgIds: state.filters.orgIds },
          currentPage: 1,
        }));
      },

      resetPagination: () => {
        set({ currentPage: 1 }); 
      },
    }),
    {
      name: 'dashboard-filters-v3',
      partialize: (state) => ({
        // `statuses` is dropped on purpose: the Active Sessions drill-down is a
        // way of arriving, not a saved preference. Persisting it would reopen
        // the browser days later on a filtered list nobody asked for.
        filters: { ...state.filters, statuses: [] },
        rowsPerPage: state.rowsPerPage,
        overviewGranularity: state.overviewGranularity,
      }),
      /**
       * Fills in filter fields the saved blob predates.
       *
       * `filters` is persisted whole, so a browser that stored it before a
       * field existed rehydrates without that field — and code reading it
       * crashes rather than falling back. Adding `statuses` would have thrown
       * `Cannot read properties of undefined` in the filter bar for every user
       * who had ever loaded the dashboard.
       *
       * Merging over DEFAULT_FILTERS makes that impossible for this field and
       * for the next one.
       */
      merge: (persisted, current) => {
        const saved = persisted as Partial<DashboardState> | undefined;
        const filters = { ...DEFAULT_FILTERS, ...(saved?.filters ?? {}) };

        // Heals a range an earlier build could persist: the Active Sessions
        // drill-down used to blank the dates and mark the preset `custom`,
        // which the overview reads as a window of now..now and renders as
        // zeros. `custom` with no dates is never a state a user can reach, so
        // finding one means it came from that bug.
        if (filters.dateRange.preset === 'custom' && filters.dateRange.from == null) {
          filters.dateRange = DEFAULT_FILTERS.dateRange;
        }

        return { ...current, ...saved, filters };
      },
    },
  ),
);

export default useDashboardStore;
